import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Paper,
  IconButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import CodeIcon from '@mui/icons-material/Code';
import { DataGrid, GridColDef, GridSortModel, GridRenderCellParams } from '@mui/x-data-grid';
import { ContainerFileInfo, listContainerFiles, downloadContainerFile } from '../../services/sftpFileRetrievalService';
import { colors } from '../../styles/colors';
import dayjs from 'dayjs';

interface ContainerFilesTableProps {
  initialFilter?: {
    pbmName?: string;
    fileNameContains?: string;
  };
}

interface ContainerFilesFilter {
  pbmName?: string;
  fileNameContains?: string;
  sortOrder?: string;
  pageSize?: number;
  pageNumber?: number;
}

const ContainerFilesTable = ({ initialFilter }: ContainerFilesTableProps) => {
  const [files, setFiles] = useState<ContainerFileInfo[]>([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filter, setFilter] = useState<ContainerFilesFilter>(() => {
    return initialFilter || {};
  });

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'lastModified',
      sort: 'desc',
    },
  ]);

  // Helper function to get the appropriate icon based on file extension
  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return <PictureAsPdfIcon sx={{ color: colors.pdf }} />;
      case 'doc':
      case 'docx':
        return <DescriptionIcon sx={{ color: colors.document }} />;
      case 'xls':
      case 'xlsx':
      case 'csv':
        return <TableChartIcon sx={{ color: colors.spreadsheet }} />;
      case 'json':
      case 'xml':
        return <CodeIcon sx={{ color: colors.code }} />;
      case 'txt':
        return <CodeIcon sx={{ color: colors.text }} />;
      default:
        return <InsertDriveFileIcon sx={{ color: colors.file }} />;
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const file = params.row as ContainerFileInfo;
        return (
          <Box>
            <IconButton
              onClick={() => handleDownload(file.containerName || '', file.fileName)}
              size="small"
              title="Download File"
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        )
      }
    },
    { 
      field: 'fileName', 
      headerName: 'File Name', 
      width: 300,
      renderCell: (params) => {
        const file = params.row as ContainerFileInfo;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getFileIcon(file.fileName)}
            <span>{file.fileName}</span>
          </Box>
        );
      }
    },
    { field: 'pbmName', headerName: 'PBM Name', width: 200 },
    {
      field: 'lastModified',
      headerName: 'Last Modified',
      width: 200,
      valueGetter: (params: GridRenderCellParams) => {
        if (!params.row) return null;
        const file = params.row as ContainerFileInfo;
        return file?.lastModified ? new Date(file.lastModified) : null;
      },
      renderCell: (params) => {
        const file = params.row as ContainerFileInfo;
        return file.lastModified 
          ? dayjs(file.lastModified).format('MMM D, YYYY h:mm A')
          : '';
      }
    },
    {
      field: 'size',
      headerName: 'Size',
      width: 120,
      renderCell: (params) => {
        // Format file size in KB, MB, or GB
        const file = params.row as ContainerFileInfo;
        const size = file.size;
        if (size < 1024) {
          return `${size} B`;
        } else if (size < 1024 * 1024) {
          return `${(size / 1024).toFixed(2)} KB`;
        } else if (size < 1024 * 1024 * 1024) {
          return `${(size / (1024 * 1024)).toFixed(2)} MB`;
        } else {
          return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
        }
      }
    },
    { field: 'containerName', headerName: 'Container', width: 200 }
  ];

  useEffect(() => {
    fetchFiles();
  }, [filter, sortModel, paginationModel]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await listContainerFiles({
        ...filter,
        sortOrder: sortModel[0]?.sort || 'desc',
        pageSize: paginationModel.pageSize,
        pageNumber: paginationModel.page + 1 // API uses 1-based indexing
      });
      
      setFiles(response.files);
      setTotalFiles(response.totalFiles);
    } catch (error) {
      console.error('Failed to fetch files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (containerName: string, fileName: string) => {
    downloadContainerFile(containerName, fileName);
  };

  const handleFilterChange = (field: keyof ContainerFilesFilter, value: string | number | null) => {
    if (field === 'fileNameContains') {
      const stringValue = value as string || '';
      setSearchText(stringValue);
      setFilter(prev => ({
        ...prev,
        [field]: (!stringValue || stringValue.length < 3) ? undefined : stringValue
      }));
      return;
    }

    setFilter(prev => ({
      ...prev,
      [field]: value || undefined
    }));
  };

  const clearFilters = () => {
    setSearchText('');
    setFilter({});
  };

  return (
    <Box>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>PBM Name</InputLabel>
            <TextField
              size="small"
              value={filter.pbmName || ''}
              onChange={(e) => handleFilterChange('pbmName', e.target.value)}
            />
          </FormControl>

          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>File Name</InputLabel>
            <TextField
              size="small"
              value={searchText}
              onChange={(e) => handleFilterChange('fileNameContains', e.target.value)}
              placeholder="Type 3 or more characters"
            />
          </FormControl>

          <IconButton
            onClick={clearFilters}
            size="small"
            title="Clear Filters"
            sx={{ mt: 4 }}
          >
            <FilterAltOffIcon />
          </IconButton>
        </Box>

        <DataGrid
          rows={files}
          columns={columns}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          sortingMode="server"
          paginationMode="server"
          rowCount={totalFiles}
          getRowId={(row) => `${row.containerName}-${row.fileName}`}
          autoHeight
        />
      </Paper>
    </Box>
  );
};

export default ContainerFilesTable;
